import classNames from 'classnames';
import React, { useMemo } from 'react';
import Icon from '@/components/atoms/icon/icon';
import Typography from '@/components/atoms/typography/typography';
import Link from '@/components/utilities/link/link';
import { MainNavigationMenuItem } from '../../main-navigation';
import styles from './main-navigation-small-menu-item.module.scss';

export interface MainNavigationSmallMenuItemProps {
  /** The class name to apply */
  className?: string;
  /** The number to show as a count */
  count?: number;
  /** Whether to show the menu count */
  hideNumbers?: boolean;
  /** The icon to show */
  icon?: IconName;
  /** Indicates whether the menu item is active */
  isActive?: boolean;
  /** Indicates whether this is a featured item */
  isFeatured?: boolean;
  /** Indicates whether this is a priority item */
  isPriority?: boolean;
  /** The list of items to show */
  items?: MainNavigationMenuItem[];
  /** The link to show */
  link: Content.Properties.Link;
  /** An event that is triggered on click */
  onClick?: (
    event?: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => void;
}

const MainNavigationSmallMenuItem = ({
  className,
  count,
  icon,
  hideNumbers = false,
  isActive = false,
  isFeatured = false,
  isPriority = false,
  items = [],
  link,
  onClick,
}: MainNavigationSmallMenuItemProps) => {
  const iconName = useMemo(() => {
    if (items.length > 0) {
      return 'ChevronDown';
    }

    if (icon) {
      return icon;
    }
  }, [icon, items]);

  const countNumber = useMemo(() => {
    if (items.length > 0) {
      return items.length;
    }

    if (count) {
      return count;
    }
  }, [count, items]);

  return (
    <Link
      className={classNames(styles.component, className, {
        [styles.isActive]: isActive,
        [styles.isFeatured]: isFeatured,
        [styles.isPriority]: isPriority,
      })}
      onClick={onClick}
      url={link.url}
    >
      <div className={styles.text}>
        <Typography tagStyle="menuItem">{link.text}</Typography>
      </div>

      {(countNumber || iconName) && (
        <div className={styles.info}>
          {countNumber && !hideNumbers && (
            <Typography className={styles.count} tagStyle="menuItem">
              {countNumber}
            </Typography>
          )}

          {iconName && (
            // Keyed by the count so the pulse replays whenever it changes
            <div
              key={countNumber}
              className={classNames(styles.controls, {
                [styles.isPulsing]: countNumber,
              })}
            >
              <Icon className={styles.icon} name={iconName} size={16} />
            </div>
          )}
        </div>
      )}
    </Link>
  );
};

MainNavigationSmallMenuItem.displayName = 'MainNavigationSmallMenuItem';

export default MainNavigationSmallMenuItem;
